import { hooksApi } from '@verifime/api-definition';
import { useMemo } from 'react';

/**
 * Options for the permission query. One field today; the next goes in when a caller needs it.
 *
 * `AddressAutocompleteView.tsx` passes the same option to the same shape of hook, so this follows a
 * pattern the package already has rather than opening up the full react-query option set.
 */
export type TUseAPIPermissionsOptions = {
  /**
   * Whether to make the request at all. Defaults to `true`.
   *
   * Pass `false` when the caller does not yet have the ids to ask with, so the hook skips the call
   * rather than querying for a customer it cannot name.
   *
   * One caveat: while the query is held back, `isLoadingPermissions` is still `true` — react-query
   * reports a query that has not run the same way it reports one still in flight. A caller that
   * needs to tell "not asked yet" from "asked, still waiting" must check its own preconditions.
   */
  enabled?: boolean;
};

export default function useAPIPermissions(
  customerId: string,
  queryOptions?: TUseAPIPermissionsOptions,
) {
  const options = useMemo(
    () => ({
      queries: {
        customerId,
      },
    }),
    [customerId],
  );

  const { data, isLoading, invalidate, error } = hooksApi.useGetV1permission(options, {
    // Configure React Query to always refetch on mount
    // This ensures we always get fresh permissions data
    refetchOnMount: 'always',
    staleTime: 0, // Always consider data stale
    ...queryOptions,
  });

  return useMemo(
    () => ({
      permissions: data || [],
      isLoadingPermissions: isLoading,
      mutatePermissions: invalidate,
      loadPermissionsError: error,
    }),
    [data, isLoading, invalidate, error],
  );
}
